import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Images, LockKeyhole, RefreshCw, Search, X } from "lucide-react";
import "./gallery-manager.css";

const readableDate = (value) => value ? new Date(value).toLocaleString() : "Not recorded";
const readableSize = (bytes) => bytes ? `${(bytes / 1048576).toFixed(1)} MB` : "Size unavailable";

/** Shared gallery workflow for authorized event photographers.
 * Private Guest uploads remain restricted to their owner, assigned photographers and Admin.
 */
export default function PhotographerGalleryManager({ eventId, photos = [], onRefresh, getOriginal }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [privacy, setPrivacy] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setQuery(""); setKind("all"); setPrivacy("all"); setSelectedId(null); setError(""); }, [eventId]);
  const counts = useMemo(() => photos.reduce((result, photo) => ({
    total: result.total + 1,
    ready: result.ready + Number(photo.status === "ready"),
    shared: result.shared + Number(photo.privacy === "shared"),
    private: result.private + Number(photo.privacy === "private")
  }), { total: 0, ready: 0, shared: 0, private: 0 }), [photos]);
  const visible = useMemo(() => photos.filter((photo) => {
    const searchable = [photo.originalFilename, photo.caption, photo.uploader?.displayName, photo.status]
      .filter(Boolean).join(" ").toLowerCase();
    return (kind === "all" || photo.kind === kind) &&
      (privacy === "all" || photo.privacy === privacy) &&
      (!query.trim() || searchable.includes(query.trim().toLowerCase()));
  }), [photos, kind, privacy, query]);
  const inspected = photos.find((photo) => photo.id === selectedId);

  async function openOriginal(photo) {
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setLoadingOriginal(true); setError("");
    try {
      const delivery = await getOriginal(eventId, photo.id, "original");
      if (tab && !tab.closed) tab.location.replace(delivery.url);
      else window.open(delivery.url, "_blank", "noopener,noreferrer");
    } catch (issue) {
      if (tab && !tab.closed) tab.close();
      setError(issue.message || "Original media access is not available.");
    } finally { setLoadingOriginal(false); }
  }

  if (!eventId) return <div className="pg-gallery-empty">Choose an event to view its shared gallery.</div>;
  return <section className="pg-gallery-manager" aria-label="Professional event gallery management">
    <div className="pg-gallery-summary">
      {[["Total", counts.total], ["Ready", counts.ready], ["Shared", counts.shared], ["Private visible", counts.private]].map(([label, value]) =>
        <article key={label}><small>{label}</small><strong>{value}</strong></article>)}
    </div>
    <div className="pg-gallery-toolbar">
      <label className="pg-gallery-search"><Search size={16} /><input aria-label="Search photographs" placeholder="Search filename, caption, uploader" value={query} onChange={(event) => setQuery(event.target.value)}/></label>
      <label>Type <select value={kind} onChange={(event) => setKind(event.target.value)}><option value="all">All types</option><option value="professional">Professional</option><option value="guest">Guest / Client shared</option></select></label>
      <label>Privacy <select value={privacy} onChange={(event) => setPrivacy(event.target.value)}><option value="all">All visible</option><option value="shared">Shared</option><option value="private">Private event uploads</option></select></label>
      <button type="button" className="button button-light" onClick={onRefresh}><RefreshCw size={16}/> Refresh</button>
      <span>{visible.length} images shown</span>
    </div>
    {error && <p role="alert" className="pg-gallery-error">{error}</p>}
    <div className={`pg-gallery-content ${inspected ? "with-inspector" : ""}`}>
      <div className="pg-gallery-grid">
        {visible.map((photo) => <article key={photo.id} className={selectedId === photo.id ? "selected" : ""}>
          <button type="button" className="pg-gallery-photo" onClick={() => setSelectedId(photo.id)} aria-label={`Inspect ${photo.originalFilename || "photo"}`}>
            <div className="pg-gallery-media">{photo.mediaUrl ? <img loading="lazy" decoding="async" src={photo.mediaUrl} alt={photo.caption || photo.originalFilename || "Event photograph"}/> : <Images size={32}/>}
              <span className={`pg-gallery-badge ${photo.privacy}`}>{photo.privacy === "private" && <LockKeyhole size={12}/>} {photo.privacy}</span></div>
            <div className="pg-gallery-meta"><strong>{photo.originalFilename || "Event photo"}</strong><small>{photo.uploader?.displayName || "Event member"} · {photo.kind}</small></div>
          </button>
        </article>)}
        {!visible.length && <div className="pg-gallery-empty">{photos.length ? "No photographs match these filters." : "No shared photographs available for this event yet."}</div>}
      </div>
      {inspected && <aside className="pg-gallery-inspector" aria-label="Photograph details">
        <button type="button" className="pg-gallery-close" onClick={() => setSelectedId(null)} aria-label="Close photo details"><X size={18}/></button>
        <div className="pg-gallery-inspector-img">{inspected.mediaUrl ? <img src={inspected.mediaUrl} alt={inspected.caption || inspected.originalFilename || "Selected photograph"}/> : <Images size={36}/>}</div>
        <h3>{inspected.originalFilename || "Selected photo"}</h3>{inspected.caption && <p>{inspected.caption}</p>}
        <dl>{[["Uploader", inspected.uploader?.displayName || "Event member"], ["Type", inspected.kind], ["Privacy", inspected.privacy], ["Status", inspected.status], ["Size", readableSize(inspected.sizeBytes)], ["Uploaded", readableDate(inspected.createdAt)]].map(([label,value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
        <button type="button" className="button button-primary" onClick={() => openOriginal(inspected)} disabled={loadingOriginal || inspected.status !== "ready"}><ExternalLink size={16}/> {loadingOriginal ? "Opening…" : "Open original"}</button>
      </aside>}
    </div>
    <p className="pg-gallery-privacy">Guest uploads stay private from other guests until explicitly shared. Assigned photographers can manage private event media.</p>
  </section>;
}
